/* eslint-disable @typescript-eslint/no-explicit-any */
import { useReportStore } from '@store';
import { usePreviewContext } from '../../../contexts/PreviewContext.ts';
import { useEffect, useRef, useState } from 'react';
import { ReportFilterEntry } from '@common/types';
import { useHandleSaveReport } from '@views/pages/Reporting/Reports/Report/hooks';
import { useLocation } from 'react-router-dom';
import { IPreviewTableFilters } from 'views/pages/Reporting/Reports/Report/components/types.ts';
import { isEqual } from 'lodash';

const TOOLBAR_ONLY_FIELDS: (keyof IPreviewTableFilters)[] = [
  'search',
  'group_by',
];

const getSanitizedFilters = (filters: IPreviewTableFilters) => {
  const sanitized = { ...filters };
  TOOLBAR_ONLY_FIELDS.forEach(field => delete sanitized[field]);
  return {
    ...sanitized,
    options: {
      ...sanitized.options,
      $and: [...(sanitized?.options?.$and ?? []).filter(item => item.value)],
    } as any,
  };
};

const areSanitizedFiltersEqual = (
  initial: IPreviewTableFilters,
  current: IPreviewTableFilters,
) =>
  isEqual(
    getSanitizedFilters({
      ...initial,
      // Use current custom_timeframe on both sides so date picker
      // initialisation differences don't cause a false mismatch
      custom_timeframe: current.custom_timeframe,
    }),
    getSanitizedFilters(current),
  );

export const useFiltersCard = () => {
  const location = useLocation();
  const {
    handleFilterChange,
    handleCloseFiltersCard,
    isFiltersCardOpen,
    filters,
  } = usePreviewContext();

  const {
    introspection,
    report,
    setReport,
    filters: reportFilters = [],
    setFilters: setReportFilters,
    hideSaveOptions,
  } = useReportStore();

  const { handleSaveReport } = useHandleSaveReport();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const initialReports = useRef(report);
  const initialReportFilters = useRef(reportFilters);
  const initialFilters = useRef(filters);
  const isAppliedRef = useRef(false);

  const handleReset = () => {
    handleFilterChange({
      ...initialFilters.current,
      // Preserve current toolbar-only fields so search/group_by are not reset
      search: filters.search,
      group_by: filters.group_by,
    });
    setReportFilters(initialReportFilters.current);
    if (initialReports?.current) {
      setReport(initialReports.current);
    }
  };

  const handleApply = () => {
    isAppliedRef.current = true;
    handleFilterChange({ ...filters });
  };

  const handleSaveFilter = (entry: ReportFilterEntry) => {
    handleFilterChange({
      ...filters,
      options: {
        ...filters.options,
        $and: [
          ...(filters?.options?.$and ?? []),
          {
            field: entry.field,
            operator: entry.operator,
            value: entry.value,
          } as any,
        ],
      } as any,
    });
    setReport({
      ...report,
      options: {
        ...report?.options,
        $and: [
          ...(report?.options?.$and ?? []),
          {
            field: entry.field,
            operator: entry.operator,
            value: entry.value,
          } as any,
        ],
      } as any,
    });
    setReportFilters([
      ...reportFilters.filter(o => o.field !== entry.field),
      entry,
    ]);
  };

  const handleRemoveFilter = (field: string) => {
    handleFilterChange({
      ...filters,
      options: {
        ...filters.options,
        $and: [
          ...(filters?.options?.$and ?? []).filter(
            item => item.field !== field,
          ),
        ],
      } as any,
    });
    setReport({
      ...report,
      options: {
        ...report?.options,
        $and: [
          ...(report?.options?.$and ?? []).filter(item => item.field !== field),
        ],
      } as any,
    });
    setReportFilters([...reportFilters.filter(o => o.field !== field)]);
  };

  const handleApplyNewFilters = () => {
    isAppliedRef.current = true;
    handleSaveReport();
  };

  useEffect(() => {
    return () => {
      if (!isAppliedRef.current) {
        handleReset();
      }
    };
  }, []);

  useEffect(() => {
    if (location?.state?.interval) {
      handleFilterChange({
        ...filters,
        interval: location.state.interval,
        ...(location.state?.interval === 'custom' && {
          custom_timeframe: {
            to_date: location.state?.to_date || null,
            from_date: location.state?.from_date || null,
          },
        }),
      });
      initialFilters.current = {
        ...filters,
        interval: location.state.interval,
        ...(location.state?.interval === 'custom' && {
          custom_timeframe: {
            to_date: location.state?.to_date || null,
            from_date: location.state?.from_date || null,
          },
        }),
      };
      setReport({
        ...report,
        interval: location.state.interval,
        ...(location.state?.interval === 'custom' && {
          custom_timeframe: {
            to_date: location.state?.to_date || null,
            from_date: location.state?.from_date || null,
          },
        }),
      });
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const isCustomDateInvalid =
    filters.interval === 'custom' &&
    (!filters.custom_timeframe?.from_date ||
      !filters.custom_timeframe?.to_date);

  return {
    report,
    setReport,
    introspection,
    filters,
    handleApply,
    handleCloseFiltersCard,
    isFiltersCardOpen,
    handleFilterChange,
    anchorEl,
    setAnchorEl,
    handleSaveFilter,
    handleRemoveFilter,
    handleApplyNewFilters,
    isApplyDisabled:
      areSanitizedFiltersEqual(initialFilters.current, filters) ||
      hideSaveOptions ||
      isCustomDateInvalid,
    isResetDisabled: areSanitizedFiltersEqual(initialFilters.current, filters),
    reportFilters,
    setReportFilters,
    handleReset,
    hideSaveOptions,
  };
};
